<template>
  <el-dialog title="选择包（可多选）" :visible.sync="dialogVisible" width="80%" @close="handleClose">
    <div style="margin-bottom: 15px;">
      <el-input
        v-model="searchKeyword"
        placeholder="搜索主包、分包编号或单位"
        @input="handleSearch"
        style="width: 300px;">
        <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
      </el-input>
      <span style="margin-left: 20px; color: #909399;">
        已选择：{{ selectedCount }} 项
      </span>
    </div>
    
    <el-table
      :data="treeData"
      v-loading="loading"
      row-key="rowKey"
      :tree-props="{children: 'children', hasChildren: 'hasChildren'}"
      :default-expand-all="true"
      @select="handleSelectionChange"
      @select-all="handleSelectAll"
      ref="packageTable"
      style="width: 100%">
      <el-table-column type="selection" width="55" :selectable="checkSelectable"></el-table-column>
      <el-table-column prop="package_name" label="包名称" min-width="200">
        <template slot-scope="scope">
          <span v-if="scope.row.isMainPackage">{{ scope.row.package_name }}</span>
          <span v-else>{{ scope.row.sub_package_name }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="sub_package_no" label="分包编号" width="160">
        <template slot-scope="scope">
          <span v-if="!scope.row.isMainPackage">{{ scope.row.sub_package_no }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="unit_name" label="项目单位" min-width="200">
        <template slot-scope="scope">
          <span v-if="scope.row.isMainPackage">{{ scope.row.unit_name }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="quantity" label="采购数量" width="120">
        <template slot-scope="scope">
          <span v-if="!scope.row.isMainPackage">{{ scope.row.quantity }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="percentage" label="百分比" width="100">
        <template slot-scope="scope">
          <span v-if="scope.row.percentage !== undefined">{{ scope.row.percentage }}%</span>
        </template>
      </el-table-column>
    </el-table>
    
    <div style="margin-top: 15px; text-align: center;">
      <el-pagination
        @current-change="handlePageChange"
        :current-page="currentPage"
        :page-size="pageSize"
        :total="total"
        layout="prev, pager, next, total">
      </el-pagination>
    </div>
    
    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" @click="handleConfirm">确定</el-button>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: 'PackageSelectDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialogVisible: false,
      packages: [],
      loading: false,
      currentPage: 1,
      pageSize: 10,
      total: 0,
      searchKeyword: '',
      selectedRows: []
    }
  },
  computed: {
    // 将扁平数据转换为树形结构
    treeData() {
      if (!this.packages || this.packages.length === 0) {
        return []
      }
      
      const packageMap = new Map()
      
      this.packages.forEach(item => {
        const packageId = item.id
        const subPackageId = item.sub_package_id
        
        if (!packageMap.has(packageId)) {
          packageMap.set(packageId, {
            rowKey: `package_${packageId}`,
            isMainPackage: true,
            id: packageId,
            package_id: packageId,
            package_name: item.package_name,
            percentage: item.percentage,
            unit_name: item.unit_name,
            project_id: item.project_id,
            children: []
          })
        }
        
        const mainPackage = packageMap.get(packageId)
        mainPackage.children.push({
          rowKey: `subpackage_${subPackageId}`,
          isMainPackage: false,
          id: subPackageId,
          package_id: packageId,
          sub_package_name: item.sub_package_name,
          sub_package_no: item.sub_package_no,
          quantity: item.quantity,
          percentage: item.percentage,
          unit_name: item.unit_name
        })
      })
      
      return Array.from(packageMap.values()).sort((a, b) => a.id - b.id)
    },
    
    selectedCount() {
      return this.selectedRows.length
    }
  },
  watch: {
    visible(val) {
      this.dialogVisible = val
      if (val) {
        this.searchKeyword = ''
        this.currentPage = 1
        this.selectedRows = []
        this.loadData()
      }
    },
    dialogVisible(val) {
      this.$emit('update:visible', val)
      if (!val) {
        // 关闭时清空选择
        this.$nextTick(() => {
          if (this.$refs.packageTable) {
            this.$refs.packageTable.clearSelection()
          }
        })
      }
    }
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          size: this.pageSize
        }
        if (this.searchKeyword) {
          params.keyword = this.searchKeyword
        }
        
        const response = await this.$http.get('/import/packages', { params })
        const data = response.data.data || response.data
        this.packages = data.items
        this.total = data.total
        
        // 恢复选择状态
        this.$nextTick(() => {
          this.restoreSelection()
        })
      } catch (error) {
        this.$message.error('加载包列表失败')
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    
    checkSelectable(row) {
      // 主包和分包都可以选择
      return true
    },
    
    handleSelectionChange(selection, row) {
      // 检查当前行是否被选中
      const isSelected = selection.some(r => r.rowKey === row.rowKey)
      const wasSelected = this.selectedRows.some(r => r.rowKey === row.rowKey)
      
      if (row.isMainPackage) {
        // 选择主包时，自动选中所有分包
        if (isSelected && !wasSelected) {
          // 选中主包，添加所有分包
          row.children.forEach(child => {
            if (!this.selectedRows.find(r => r.rowKey === child.rowKey)) {
              this.selectedRows.push(child)
            }
            this.$refs.packageTable.toggleRowSelection(child, true)
          })
          // 添加主包
          if (!this.selectedRows.find(r => r.rowKey === row.rowKey)) {
            this.selectedRows.push(row)
          }
        } else if (!isSelected && wasSelected) {
          // 取消主包，移除所有分包
          row.children.forEach(child => {
            const index = this.selectedRows.findIndex(r => r.rowKey === child.rowKey)
            if (index > -1) {
              this.selectedRows.splice(index, 1)
            }
            this.$refs.packageTable.toggleRowSelection(child, false)
          })
          // 移除主包
          const index = this.selectedRows.findIndex(r => r.rowKey === row.rowKey)
          if (index > -1) {
            this.selectedRows.splice(index, 1)
          }
        }
      } else {
        // 选择分包
        if (isSelected && !wasSelected) {
          if (!this.selectedRows.find(r => r.rowKey === row.rowKey)) {
            this.selectedRows.push(row)
          }
        } else if (!isSelected && wasSelected) {
          const index = this.selectedRows.findIndex(r => r.rowKey === row.rowKey)
          if (index > -1) {
            this.selectedRows.splice(index, 1)
          }
        }
      }
    },
    
    handleSelectAll(selection) {
      // 全选/取消全选
      if (selection.length > 0) {
        // 全选
        this.treeData.forEach(mainPackage => {
          if (!this.selectedRows.find(r => r.rowKey === mainPackage.rowKey)) {
            this.selectedRows.push(mainPackage)
          }
          mainPackage.children.forEach(child => {
            if (!this.selectedRows.find(r => r.rowKey === child.rowKey)) {
              this.selectedRows.push(child)
            }
          })
        })
      } else {
        // 取消全选
        this.selectedRows = []
      }
    },
    
    restoreSelection() {
      // 恢复选择状态
      if (this.$refs.packageTable && this.selectedRows.length > 0) {
        this.treeData.forEach(mainPackage => {
          const mainSelected = this.selectedRows.find(r => r.rowKey === mainPackage.rowKey)
          if (mainSelected) {
            this.$refs.packageTable.toggleRowSelection(mainPackage, true)
          }
          mainPackage.children.forEach(child => {
            const childSelected = this.selectedRows.find(r => r.rowKey === child.rowKey)
            if (childSelected) {
              this.$refs.packageTable.toggleRowSelection(child, true)
            }
          })
        })
      }
    },
    
    handleSearch() {
      this.currentPage = 1
      this.loadData()
    },
    
    handlePageChange(page) {
      this.currentPage = page
      this.loadData()
    },
    
    handleConfirm() {
      if (this.selectedRows.length === 0) {
        this.$message.warning('请至少选择一个包')
        return
      }
      
      // 整理选中的数据：主包ID和分包ID列表
      // 如果选择了主包，只传递主包ID（不传递该主包下的分包ID）
      const packageIds = []
      const subPackageIds = []
      const selectedPackageIds = new Set()
      
      // 先收集所有选中的主包ID
      this.selectedRows.forEach(row => {
        if (row.isMainPackage) {
          selectedPackageIds.add(row.package_id)
        }
      })
      
      // 收集主包ID和不在已选主包下的分包ID
      this.selectedRows.forEach(row => {
        if (row.isMainPackage) {
          packageIds.push(row.package_id)
        } else {
          // 如果该分包所属的主包没有被选中，则添加分包ID
          if (!selectedPackageIds.has(row.package_id)) {
            subPackageIds.push(row.id)
          }
        }
      })
      
      this.$emit('select', {
        packageIds: packageIds,
        subPackageIds: subPackageIds,
        selectedRows: this.selectedRows
      })
      this.dialogVisible = false
    },
    
    handleClose() {
      this.dialogVisible = false
    }
  }
}
</script>
